const finiteNumber = (value) => {
  if (value === "" || value === null || value === undefined || typeof value === "boolean") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const emptyReadiness = Object.freeze({
  valid_for_publish: false,
  error_count: 0,
  warning_count: 0,
  errors: [],
  warnings: [],
});

export function issueMessage(issue, lang = "id") {
  return (lang === "id" ? issue?.message_id : issue?.message_en)
    || issue?.message_en
    || issue?.message_id
    || issue?.code
    || "Validation issue";
}

export function validationFromApiDetail(detail) {
  if (!detail || typeof detail !== "object" || detail.code !== "instrument_not_publishable") return null;
  return {
    valid_for_publish: !!detail.valid_for_publish,
    error_count: Number(detail.error_count) || 0,
    warning_count: Number(detail.warning_count) || 0,
    errors: Array.isArray(detail.errors) ? detail.errors : [],
    warnings: Array.isArray(detail.warnings) ? detail.warnings : [],
  };
}

export async function persistInstrumentWithPublishGuard({ client, instrument, publish, allowWarnings = false, isNew, instrumentId }) {
  let validation = null;
  if (publish) {
    validation = (await client.post("/admin/instrument-validation", instrument)).data;
    if (!validation?.valid_for_publish) return { kind: "blocked", validation, source: "preview" };
    if (validation.warning_count > 0 && !allowWarnings) return { kind: "warnings", validation, source: "preview" };
  }

  const payload = { ...instrument, status: publish ? "published" : "draft" };
  try {
    const response = isNew
      ? await client.post("/admin/instruments", payload)
      : await client.put(`/admin/instruments/${instrumentId}`, payload);
    return { kind: "saved", response, validation };
  } catch (error) {
    const backendValidation = validationFromApiDetail(error?.response?.data?.detail);
    if (backendValidation) return { kind: "blocked", validation: backendValidation, source: "backend" };
    throw error;
  }
}

const OPTION_RANGE_TYPES = ["likert", "single_choice", "yes_no", "dropdown", "matrix"];

// Mirrors backend/reverse_scoring.py: option-based items reverse within their own
// lowest..highest option score; other items fall back to the instrument scale.
export function reverseBounds(question, scoring = {}) {
  const scores = (question?.options || [])
    .map((option) => finiteNumber(option?.score ?? 0))
    .filter((score) => score !== null);
  if (OPTION_RANGE_TYPES.includes(question?.question_type || "likert") && new Set(scores).size >= 2) {
    return { min: Math.min(...scores), max: Math.max(...scores), source: "options" };
  }
  return { min: finiteNumber(scoring.scale_min), max: finiteNumber(scoring.scale_max), source: "scale" };
}

export function buildReversePreview(question, scoring = {}) {
  const reverseRows = (question?.matrix_rows || []).filter((row) => row?.reverse_scored);
  const enabled = !!question?.reverse_scored || reverseRows.length > 0;
  if (!enabled) return null;

  const bounds = reverseBounds(question, scoring);
  const scaleMin = bounds.min;
  const scaleMax = bounds.max;
  const base = {
    formula: bounds.source === "options" ? "(option_max + option_min) - score" : "(scale_max + scale_min) - score",
    rangeSource: bounds.source,
    scaleMin,
    scaleMax,
    reverseRows: reverseRows.map((row) => row.code || row.label_id || row.label_en || row.id).filter(Boolean),
    aggregateMultipleChoice: question?.question_type === "multiple_choice" && !!question?.reverse_scored,
  };
  if (scaleMin === null || scaleMax === null || scaleMin >= scaleMax) {
    return { ...base, available: false, reason: "invalid_scale", mappings: [] };
  }

  const mappings = (question?.options || []).map((option) => {
    const score = finiteNumber(option?.score);
    if (score === null) return null;
    return {
      id: option.id,
      code: option.code || "",
      label_id: option.label_id || "",
      label_en: option.label_en || "",
      score,
      reversedScore: (scaleMax + scaleMin) - score,
    };
  }).filter(Boolean);
  return {
    ...base,
    available: mappings.length > 0,
    reason: mappings.length ? null : "no_numeric_scores",
    mappings,
  };
}
